import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowUpRight } from "lucide-react";
import { POSTS } from "@/data/journal";
import { useSeo } from "@/lib/seo";

const PAGE_META = {
  title: "The Journal · Beginner Golf Tips, Coaching & Gear · Golf Start",
  description:
    "Editorial guides from Golf Start: beginner tips, equipment advice, range etiquette, how to choose a coach, and what to expect from your first lessons.",
};

function FeaturedCard({ post }) {
  return (
    <Link
      to={`/journal/${post.slug}`}
      data-testid={`journal-featured-${post.slug}`}
      className="group grid lg:grid-cols-12 gap-8 lg:gap-12 items-end border-b border-cream-border pb-12 mb-12"
    >
      <div className="lg:col-span-7 relative aspect-[16/10] overflow-hidden bg-forest">
        <img
          src={post.image}
          alt={post.title}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
        />
      </div>
      <div className="lg:col-span-5">
        <div className="flex items-center gap-4 text-xs tracking-[0.25em] uppercase text-forest-muted">
          <span className="text-gold">{post.category}</span>
          <span className="h-px w-6 bg-cream-border" />
          <span>{post.readTime}</span>
        </div>
        <h2 className="mt-5 font-serif text-4xl md:text-5xl leading-[1.05] text-forest group-hover:text-gold transition-colors">
          {post.title}
        </h2>
        <p className="mt-5 font-sans text-base leading-relaxed text-forest-muted">
          {post.excerpt}
        </p>
        <div className="mt-7 inline-flex items-center gap-2 font-sans text-xs tracking-[0.25em] uppercase text-forest-dark group-hover:text-gold transition-colors">
          Read the article
          <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
        </div>
      </div>
    </Link>
  );
}

function ArticleCard({ post }) {
  return (
    <Link
      to={`/journal/${post.slug}`}
      data-testid={`journal-card-${post.slug}`}
      className="group flex flex-col bg-cream-light border border-cream-border overflow-hidden transition-transform duration-500 ease-out hover:-translate-y-1 hover:border-gold"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-forest">
        <img
          src={post.image}
          alt={post.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
      </div>
      <div className="flex flex-1 flex-col p-7">
        <div className="flex items-center gap-4 text-[10px] tracking-[0.25em] uppercase text-forest-muted">
          <span className="text-gold">{post.category}</span>
          <span className="h-px w-5 bg-cream-border" />
          <span>{post.readTime}</span>
        </div>
        <h3 className="mt-4 font-serif text-2xl leading-tight text-forest group-hover:text-gold transition-colors">
          {post.title}
        </h3>
        <p className="mt-3 font-sans text-sm leading-relaxed text-forest-muted line-clamp-3">
          {post.excerpt}
        </p>
        <div className="mt-6 pt-5 border-t border-cream-border flex items-center justify-between">
          <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-forest-dark group-hover:text-gold transition-colors">
            Read more
          </span>
          <span className="font-sans text-xs text-forest-muted">{post.date}</span>
        </div>
      </div>
    </Link>
  );
}

export default function Journal() {
  useSeo(PAGE_META);
  const [featured, ...rest] = POSTS;

  return (
    <div className="min-h-screen bg-cream" data-testid="journal-page">
      <Header />

      {/* Hero */}
      <section className="border-b border-cream-border">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="eyebrow mb-6">The Journal — Field Notes for Beginners</div>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-forest">
                Learn the game,{" "}
                <em className="italic text-gold font-light">one essay at a time.</em>
              </h1>
              <p className="mt-8 max-w-xl font-sans text-base md:text-lg leading-relaxed text-forest-muted">
                Beginner-first guides from working coaches — fundamentals, gear, etiquette, and the
                small habits that quietly drop strokes. No theatrics. Just what actually works.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <div className="rule md:ml-auto mb-4" />
              <div className="eyebrow">Updated Weekly</div>
              <div className="mt-2 font-serif text-xl text-forest-dark">
                {POSTS.length} articles · San Francisco
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pt-16 md:pt-20">
        <div className="eyebrow mb-8">Featured</div>
        <FeaturedCard post={featured} />
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pb-16 md:pb-24">
        <div className="eyebrow mb-8">More from the Journal</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {rest.map((p) => (
            <ArticleCard key={p.slug} post={p} />
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
